import React from 'react';
import { en } from '../locales/en';
import { stripInstitutionalMaturityFromExecutiveSummary } from '../utils/executiveSummaryDisplay';
import { accreditationLevelLabel, resolveCycleAccreditationLevel } from '../utils/accreditationLevel';

/** Renders roadmap body: prose paragraphs, then lines starting with "1. " / "2) " as a numbered list (one item per line). */
function StrategicRoadmapFormatted({ content }) {
  const text = content != null ? String(content).trim() : '';
  if (!text) return <span>—</span>;

  const lines = text.split(/\n/);
  const blocks = [];
  let proseBuf = [];
  let listBuf = [];

  const flushProse = () => {
    if (!proseBuf.length) return;
    const joined = proseBuf.join('\n').trim();
    proseBuf = [];
    if (joined) {
      blocks.push(
        <p key={`prose-${blocks.length}`} style={{ margin: '0 0 14px 0', whiteSpace: 'pre-wrap', color: '#334155' }}>
          {joined}
        </p>
      );
    }
  };

  const flushList = () => {
    if (!listBuf.length) return;
    const items = listBuf.map((raw) => raw.replace(/^\s*\d+[\.\)]\s*/, '').trim()).filter(Boolean);
    listBuf = [];
    if (!items.length) return;
    blocks.push(
      <ol
        key={`ol-${blocks.length}`}
        style={{
          margin: '0 0 16px 0',
          paddingLeft: '1.35rem',
          color: '#334155',
          listStylePosition: 'outside'
        }}
      >
        {items.map((item, i) => (
          <li key={i} style={{ marginBottom: '10px', whiteSpace: 'pre-wrap' }}>
            {item}
          </li>
        ))}
      </ol>
    );
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (/^\d+[\.\)]\s+/.test(trimmed)) {
      flushProse();
      listBuf.push(trimmed);
    } else {
      flushList();
      proseBuf.push(line);
    }
  }
  flushList();
  flushProse();

  return <div>{blocks.length ? blocks : <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>}</div>;
}

function formatGapsText(gaps) {
  if (gaps == null) return '—';
  if (typeof gaps === 'string') return gaps.trim() || '—';
  if (Array.isArray(gaps)) return gaps.filter(Boolean).join('\n') || '—';
  return '—';
}

/**
 * Read-only final report body (archive snapshot or legacy live snapshot).
 * Mirrors ReportEditor preview order; no inputs or workflow controls.
 */
const ArchivedReportView = ({
  report,
  audit,
  schoolProfile,
  distilledData,
  benchmarkReadiness,
  showBenchmarkingSection = true
}) => {
  if (!report) return null;

  const getLevelLabel = (stationId, level) => {
    if (!level) return 'Not Assessed';
    const stationData = en.stations.find((s) => s.id === stationId);
    return stationData?.diagnosticGrid[level]?.label || `Level ${level}`;
  };

  const getComparisonIcon = (self, ifip) => {
    if (!self || !ifip) return <span style={{ color: '#cbd5e1' }}>?</span>;
    const s = parseInt(self, 10);
    const i = parseInt(ifip, 10);
    if (i > s) return <span style={{ color: '#4ade80', fontWeight: 'bold' }}>↑</span>;
    if (i < s) return <span style={{ color: '#f87171', fontWeight: 'bold' }}>↓</span>;
    return <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>=</span>;
  };

  const rawTier = resolveCycleAccreditationLevel(report, schoolProfile);
  const accLevel = accreditationLevelLabel(rawTier, { long: true });
  const globalAvg = 3.0;
  const score = report.meta?.overallMaturityScore ?? 0;
  const { ready: benchmarkReady, minSampleSize, sampleCount } = benchmarkReadiness || {};

  const card = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '24px',
    marginBottom: '20px',
    color: '#1e293b'
  };

  const h3 = {
    margin: '0 0 16px 0',
    fontSize: '0.75rem',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: '#138a8a',
    fontWeight: 800
  };

  return (
    <div className="archived-report-view" style={{ fontFamily: 'system-ui, sans-serif', lineHeight: 1.5 }}>
      <section style={card}>
        <h3 style={h3}>01. Institutional Brief</h3>
        {report.meta?.overallMaturityScore != null && (
          <div
            style={{
              background: 'rgba(19, 138, 138, 0.08)',
              border: '1px solid #138a8a',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}
          >
            <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#138a8a' }}>
              {Number(report.meta.overallMaturityScore).toFixed(2)}
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 'bold', color: '#138a8a' }}>
                Institutional Maturity Score
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                Calculated average across all 7 framework stations based on clinical audit data.
              </div>
            </div>
          </div>
        )}
        <h4 style={{ margin: '0 0 8px 0', color: '#1a2a44' }}>
          Portrait of {schoolProfile?.officialName || 'School'}
        </h4>
        <div style={{ fontSize: '0.95rem', color: '#334155', whiteSpace: 'pre-wrap' }}>
          {stripInstitutionalMaturityFromExecutiveSummary(report.institutionalBrief?.executiveSummary || '') || 'No portrait provided.'}
        </div>

        <h4 style={{ margin: '24px 0 8px 0', color: '#1a2a44' }}>Summary of Maturity Ratings</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '8px' }}>Station</th>
                <th style={{ textAlign: 'center', padding: '8px', width: 56 }}>SEF</th>
                <th style={{ width: 40 }} />
                <th style={{ textAlign: 'left', padding: '8px', color: '#d11d7e' }}>IFIP Maturity Rating</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6, 7].map((num) => {
                const sId = `0${num}`;
                const selfLvl = audit?.stations?.[sId]?.selectedLevel;
                const ifipLvl = report.stationAnalysis?.[sId]?.ifipRating;
                return (
                  <tr key={sId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px' }}>
                      <strong>{en.stations[num - 1].title}</strong>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#64748b' }}>{selfLvl || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{getComparisonIcon(selfLvl, ifipLvl)}</td>
                    <td style={{ fontWeight: 'bold', color: '#d11d7e', padding: '8px' }}>
                      {ifipLvl ? getLevelLabel(sId, ifipLvl) : 'Awaiting assessment...'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {!showBenchmarkingSection && (
      <section style={{ ...card, padding: '16px 24px', background: '#f1f5f9' }}>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
          <strong>02. Maturity Matrix &amp; Benchmarking</strong> — comparative metrics are summarised in your portal&apos;s
          &quot;Global benchmarking&quot; panel above this report.
        </p>
      </section>
      )}

      {showBenchmarkingSection && (
      <section style={card}>
        <h3 style={h3}>02. Maturity Matrix &amp; Benchmarking</h3>
        {benchmarkReady ? (
          <div
            style={{
              background: 'rgba(19, 138, 138, 0.06)',
              padding: '22px',
              borderRadius: '8px',
              border: '1px solid rgba(19, 138, 138, 0.25)'
            }}
          >
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#138a8a', fontWeight: 700, marginBottom: '12px' }}>
              Performance vs. global mean (aggregated sample)
            </div>
            <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', position: 'relative', marginTop: '28px' }}>
              <div
                style={{
                  position: 'absolute',
                  left: `${(globalAvg / 5) * 100}%`,
                  top: '-20px',
                  textAlign: 'center',
                  transform: 'translateX(-50%)'
                }}
              >
                <div style={{ width: '2px', height: '24px', background: '#fbbf24', margin: '0 auto' }} />
                <span style={{ fontSize: '0.55rem', color: '#ca8a04', fontWeight: 'bold' }}>GLOBAL AVG ({globalAvg})</span>
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: `${Math.min(100, Math.max(0, (score / 5) * 100))}%`,
                  top: '-6px',
                  width: '20px',
                  height: '20px',
                  background: '#d11d7e',
                  borderRadius: '50%',
                  transform: 'translateX(-50%)',
                  border: '3px solid #fff',
                  boxShadow: '0 0 12px rgba(209, 29, 126, 0.35)',
                  zIndex: 2
                }}
              />
            </div>
            <p style={{ fontSize: '0.88rem', color: '#475569', marginTop: '20px', marginBottom: 0 }}>
              This institution is performing <strong style={{ color: '#1e293b' }}>{(score - globalAvg).toFixed(2)}</strong> points
              relative to the global average for <strong style={{ color: '#138a8a' }}>{accLevel}</strong> institutions (sample:{' '}
              {sampleCount} published reports; threshold {minSampleSize}).
            </p>
          </div>
        ) : (
          <div
            style={{
              padding: '36px',
              textAlign: 'center',
              border: '1px dashed #cbd5e1',
              borderRadius: '12px',
              background: '#f8fafc'
            }}
          >
            <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>📊</div>
            <h4 style={{ margin: '0 0 10px 0', color: '#1e293b', fontSize: '1rem' }}>Global Benchmarking Aggregation</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', maxWidth: '520px', marginLeft: 'auto', marginRight: 'auto' }}>
              IFIP is aggregating anonymised maturity data across the network. Comparative analysis will appear here once the
              published sample reaches the configured threshold (currently {minSampleSize ?? 100}; {sampleCount ?? 0} on file).
            </p>
          </div>
        )}
      </section>
      )}

      <section style={{ ...card, background: '#f8fafc' }}>
        <h3 style={h3}>03. Station-Based Analysis</h3>
        <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 0, marginBottom: '18px' }}>
          IFIP clinical findings by station (school self-evaluation narratives are not reproduced in this issued view).
        </p>
        {[1, 2, 3, 4, 5, 6, 7].map((num) => {
          const sId = `0${num}`;
          const stationMeta = en.stations[num - 1];
          const ifipData = report.stationAnalysis?.[sId] || { ifipRating: '', content: '' };
          return (
            <div
              key={sId}
              style={{
                padding: '18px 0',
                borderBottom: num < 7 ? '1px solid #e2e8f0' : 'none'
              }}
            >
              <div style={{ fontSize: '0.65rem', color: '#d11d7e', textTransform: 'uppercase', marginBottom: '8px' }}>
                Station {sId} — {stationMeta.title}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#1e293b', marginBottom: '8px' }}>
                <strong>IFIP maturity rating:</strong> {ifipData.ifipRating ? getLevelLabel(sId, ifipData.ifipRating) : '—'}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#334155', whiteSpace: 'pre-wrap' }}>
                {ifipData.content || 'Analysis pending...'}
              </div>
            </div>
          );
        })}
      </section>

      <section style={card}>
        <h3 style={h3}>04. Evidence Portfolio &amp; Gaps</h3>
        <h4 style={{ margin: '0 0 12px 0', color: '#1a2a44', fontSize: '0.9rem' }}>Evidence vault registry</h4>
        {(() => {
          const vault = audit?.evidenceVault || [];
          const factSheet = distilledData?.factSheet || [];
          const reg = report.evidencePortfolio?.registry;
          const useRegistry = Array.isArray(reg) && reg.length > 0;
          const rows = useRegistry
            ? reg.map((row) => ({
                name: row.fileName || row.name || '—',
                category: row.category || 'General',
                summary: row.evidenceSummary || row.summary || '',
                proves: row.proves || ''
              }))
            : vault.map((file) => {
                const summary = factSheet.find((f) => f.fileName === file.name);
                return {
                  name: file.name,
                  category: summary?.category || 'General',
                  summary: summary?.evidenceSummary || '',
                  proves: summary?.proves || ''
                };
              });

          if (!rows.length) {
            return (
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '16px' }}>
                No indexed evidence rows on file for this snapshot (vault may be empty or distillation not yet linked).
              </p>
            );
          }

          return (
            <div style={{ marginBottom: '22px' }}>
              {rows.map((row, idx) => (
                <div
                  key={`${row.name}-${idx}`}
                  style={{
                    padding: '12px',
                    background: 'rgba(15, 23, 42, 0.03)',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '10px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', gap: '8px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#0f172a' }}>{row.name}</span>
                    <span style={{ fontSize: '0.65rem', color: '#138a8a', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {row.category}
                    </span>
                  </div>
                  {row.summary ? (
                    <div style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.45 }}>{row.summary}</div>
                  ) : null}
                  {row.proves ? (
                    <div style={{ fontSize: '0.72rem', color: '#138a8a', marginTop: '6px' }}>
                      <strong>Verification:</strong> {row.proves}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          );
        })()}

        <h4 style={{ margin: '0 0 10px 0', color: '#1a2a44', fontSize: '0.9rem' }}>Gaps &amp; omissions</h4>
        <div style={{ fontSize: '0.92rem', color: '#334155', whiteSpace: 'pre-wrap' }}>{formatGapsText(report.evidencePortfolio?.gaps)}</div>
      </section>

      <section style={card}>
        <h3 style={h3}>05. Strategic Roadmap</h3>
        <h4 style={{ margin: '0 0 8px 0', color: '#1a2a44' }}>Conclusion &amp; recommendations</h4>
        <div style={{ fontSize: '0.92rem', color: '#334155', marginBottom: '16px' }}>
          <StrategicRoadmapFormatted content={report.strategicRoadmap?.content} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <h4 style={{ fontSize: '0.8rem', color: '#138a8a', margin: '0 0 6px 0' }}>Next review &amp; progression</h4>
            <div style={{ fontSize: '0.88rem', color: '#475569', whiteSpace: 'pre-wrap' }}>
              {report.strategicRoadmap?.nextPhase || '—'}
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: '0.8rem', color: '#138a8a', margin: '0 0 6px 0' }}>IFIP resource matching</h4>
            <div style={{ fontSize: '0.88rem', color: '#475569', whiteSpace: 'pre-wrap' }}>
              {report.strategicRoadmap?.resources || '—'}
            </div>
          </div>
        </div>
      </section>

      <section style={{ ...card, background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155' }}>
        <h3 style={{ ...h3, color: '#138a8a' }}>06. Certification &amp; Sign-Off</h3>
        <div style={{ padding: '18px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '8px' }}>Lead accreditor</div>
          <div style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#fff', marginBottom: '6px' }}>
            {report.certification?.accreditorName || schoolProfile?.assignedAuditorName || '—'}
          </div>
          <div style={{ fontSize: '0.82rem', color: '#138a8a', fontWeight: 'bold', marginBottom: '10px' }}>
            {report.certification?.accreditorQuals || ''}
          </div>
          <div style={{ fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.6 }}>
            {report.certification?.accreditorBio || ''}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArchivedReportView;
